import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../lib/utils';

/**
 * Button — primary, secondary, ghost, destructive variants.
 * Sizes: sm (h-8), md (h-9), lg (h-10), icon (square).
 */
const buttonVariants = cva(
  // Base — all buttons share these
  [
    'inline-flex items-center justify-center gap-2',
    'font-medium whitespace-nowrap',
    'transition-colors duration-150',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
    'disabled:opacity-50 disabled:pointer-events-none',
  ].join(' '),
  {
    variants: {
      variant: {
        primary:
          'bg-brand text-brand-fg hover:bg-brand/90 active:bg-brand/80 shadow-sm',
        secondary:
          'bg-surface-2 text-fg border border-border hover:bg-surface-3 hover:border-border-strong',
        ghost:
          'text-fg-muted hover:text-fg hover:bg-surface-2',
        outline:
          'border border-border text-fg hover:bg-surface-2 hover:border-border-strong',
        destructive:
          'bg-destructive/10 text-destructive border border-destructive/20 hover:bg-destructive/20',
      },
      size: {
        sm: 'h-8 px-3 text-xs rounded-md',
        md: 'h-9 px-4 text-sm rounded-md',
        lg: 'h-11 px-6 text-base rounded-lg',
        icon: 'h-9 w-9 rounded-md',
        'icon-sm': 'h-7 w-7 rounded-md',
      },
    },
    defaultVariants: {
      variant: 'secondary',
      size: 'md',
    },
  }
);

export const Button = React.forwardRef(function Button(
  { className, variant, size, type = 'button', children, ...props },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    >
      {children}
    </button>
  );
});